import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { Loader2, LockKeyhole, Eye, EyeOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/lib/auth";
import { errMsg } from "@/lib/api";
import { BrandLogo } from "@/components/Brand";

const IMG = "https://images.unsplash.com/photo-1621905251918-48416bd8575a?crop=entropy&cs=srgb&fm=jpg&q=80&w=1400";

export default function LoginPage() {
  const { user, login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [remember, setRemember] = useState(true);

  if (user) return <Navigate to={user.role === "admin" ? "/admin" : sessionStorage.getItem("ms_punch_k") ? "/tecnico/timbra" : "/tecnico"} replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const u = await login(email, password, remember);
      nav(u.role === "tecnico" ? (sessionStorage.getItem("ms_punch_k") ? "/tecnico/timbra" : "/tecnico") : "/admin");
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid min-h-screen lg:grid-cols-2">
      <div className="relative hidden lg:block bg-[#0f1710] grain">
        <img src={IMG} alt="" className="absolute inset-0 h-full w-full object-cover opacity-40" />
        <div className="relative flex h-full flex-col justify-end p-12 text-white">
          <div className="flex flex-col items-start gap-4 rise" data-testid="login-hero-brand">
            <img src="/brand/logo.png" alt="Madonna Store" className="h-16 w-auto drop-shadow-lg" data-testid="login-hero-logo" />
            <h2 className="font-display text-4xl font-bold">App Interventi</h2>
          </div>
        </div>
      </div>
      <div className="flex flex-col px-6 py-8 sm:px-12">
        <div className="hidden lg:block"><BrandLogo /></div>
        <div className="lg:hidden flex flex-col items-start gap-3 rounded-2xl bg-[#1A2B3C] p-6 text-white rise" data-testid="login-mobile-brand">
          <img src="/brand/logo.png" alt="Madonna Store" className="h-12 w-auto" data-testid="login-mobile-logo" />
          <p className="font-display text-2xl font-bold">App Interventi</p>
        </div>
        <form onSubmit={submit} className="m-auto w-full max-w-sm space-y-6 rise" data-testid="login-form">
          <div>
            <LockKeyhole className="h-8 w-8 text-[#61a229]" />
            <h1 className="mt-4 font-display text-3xl font-bold">Area riservata</h1>
            <p className="mt-1 text-sm text-slate-500">Accesso per amministratori e tecnici.</p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input id="email" type="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="login-email-input" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="password">Password</Label>
            <div className="relative">
              <Input id="password" type={showPwd ? "text" : "password"} autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} required className="pr-11" data-testid="login-password-input" />
              <button type="button" onClick={() => setShowPwd((v) => !v)} aria-label={showPwd ? "Nascondi password" : "Mostra password"}
                className="absolute inset-y-0 right-0 grid w-11 place-items-center text-slate-400 hover:text-slate-700 transition-colors" data-testid="login-password-toggle">
                {showPwd ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm text-slate-600" data-testid="login-remember">
            <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} className="h-4 w-4 accent-[#61a229]" data-testid="login-remember-checkbox" />
            Ricordami su questo dispositivo
          </label>
          {error && <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="login-error">{error}</p>}
          <Button type="submit" disabled={busy} className="w-full rounded-full" data-testid="login-submit-button">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} Accedi
          </Button>
        </form>
      </div>
    </div>
  );
}
